<template>
    <router-link
        v-if="canOpen"
        :to="{ name: 'product-preview', params: { id: productId } }"
        target="_blank"
        :title="$t('Open the product sheet')"
        @click.stop
    >
        <b-icon icon="open-in-new" size="is-small" />
    </router-link>
</template>

<script setup>
import { computed } from 'vue';
import { useOrgStore } from '@/stores/org';

/**
 * The one way to reach a product sheet from a line of a document.
 *
 * It opens in a new tab on purpose: the editable line tables live inside forms,
 * and following a link in place would throw away lines nobody had saved yet.
 *
 * Products is an optional module, and the front registers every module's routes
 * whether or not the organization enabled it (resources/js/modules/index.js) —
 * so a link written by hand leads either to a screen the API answers 403 on, or,
 * for a user without the ability, to a silent redirect to the dashboard. Asking
 * here means no caller has to remember.
 */
const props = defineProps({
    productId: { type: [Number, String], default: null }
});

const orgStore = useOrgStore();

/**
 * `canAny` rather than `can`: the route guard itself accepts any `products.*`
 * ability (resources/js/routes.js), so a read-only user must keep the link.
 *
 * Both getters answer true until the org is loaded, so the icon shows on the
 * first paint and settles a moment later — the same permissiveness the sidebar
 * and the route guard already live with.
 */
const canOpen = computed(
    () => !!props.productId && orgStore.isModuleEnabled('products') && orgStore.canAny('products')
);
</script>
